/**
 * Local development login bypass — frontend contract.
 * ---------------------------------------------------
 * When `VITE_DEV_LOGIN=true` (only set in .env.example for local dev), the
 * app skips Keycloak entirely: the sign-in card's buttons open the
 * DevLoginScreen where Keycloak's own page would be, the user picks a role
 * there, and the SPA sends a recognisable dev token instead of a real JWT.
 *
 * This must stay in lock-step with the API side (Api/auth/dev_bypass.py) and
 * the seeded users (Database/flyway/seeds/V8__seed_dev_bypass_users.sql):
 *   Authorization: Bearer devbypass.<roleKey>
 *
 * The flag is compiled from an env var, so a production build with the flag
 * unset tree-shakes this into a no-op (`DEV_LOGIN_ENABLED === false`).
 */

export type DevRoleKey =
  | 'global-admin'
  | 'tenant-admin'
  | 'coach'
  | 'zwem-member'
  | 'zwem-member-remote';

/** Runtime guard: is the page actually being served from a localhost origin? */
const isLocalHostname = (): boolean => {
  if (typeof window === 'undefined' || !window.location) return false;
  const h = window.location.hostname;
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '0.0.0.0' ||
    h === '::1' ||
    h === '[::1]'
  );
};

/**
 * Enabled only under THREE independent conditions, each of which defaults to
 * the safe (off) state — so the bypass cannot ship to or activate in production:
 *   1. VITE_DEV_LOGIN === 'true'  — env var, unset in production builds.
 *   2. IS_LOCALHOST               — build-time constant (vite.config.ts); false
 *      whenever the app is built for a real domain (DOMAIN_SELF set), so a
 *      production build statically tree-shakes every dev-login branch away.
 *   3. isLocalHostname()          — the page is actually served from localhost.
 */
export const DEV_LOGIN_ENABLED =
  import.meta.env.VITE_DEV_LOGIN === 'true' && IS_LOCALHOST && isLocalHostname();

/** Bearer-token prefix recognised by the API dev bypass. */
export const DEV_TOKEN_PREFIX = 'devbypass.';

/** Where the dev stub's sign-in and sign-up go: the DevLoginScreen, in Keycloak's place. */
export const DEV_LOGIN_PATH = '/dev-login';

/** localStorage key holding the currently selected dev role. */
const STORAGE_KEY = 'mlp_dev_role';

export interface DevRoleOption {
  key: DevRoleKey;
  /** Translation key of the name shown on the picker card and the dev badge. */
  labelKey: string;
  /** Display name used for the fake session (header greeting). */
  firstName: string;
  lastName: string;
}

/**
 * The five personas, in the order a reader meets them: the platform, one
 * organisation, a coach inside it, a member of its programme, and the same kind
 * of member whose values live at a provider instead of here.
 *
 * The last two are the pair worth having. They follow the same programme and
 * differ in one thing — where the measurement is stored — which is the
 * distinction the federation exists to make and the one a single member cannot
 * show.
 */
export const DEV_ROLES: readonly DevRoleOption[] = [
  {
    key: 'global-admin',
    labelKey: 'devLogin.roles.globalAdmin',
    firstName: 'Dev',
    lastName: 'Global Admin',
  },
  {
    key: 'tenant-admin',
    labelKey: 'devLogin.roles.tenantAdmin',
    firstName: 'Dev',
    lastName: 'Tenant Admin',
  },
  {
    key: 'coach',
    labelKey: 'devLogin.roles.coach',
    firstName: 'Dev',
    lastName: 'Coach',
  },
  {
    key: 'zwem-member',
    labelKey: 'devLogin.roles.member',
    firstName: 'Dev',
    lastName: 'ZWEM Member',
  },
  {
    key: 'zwem-member-remote',
    labelKey: 'devLogin.roles.remoteMember',
    firstName: 'Dev',
    lastName: 'ZWEM Member (remote)',
  },
] as const;

const isDevRoleKey = (value: string | null): value is DevRoleKey =>
  DEV_ROLES.some((role) => role.key === value);

/** The role selected for this session, or null when none has been picked yet. */
export const getDevRole = (): DevRoleKey | null => {
  if (!DEV_LOGIN_ENABLED) return null;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isDevRoleKey(stored) ? stored : null;
  } catch {
    return null;
  }
};

export const setDevRole = (role: DevRoleKey): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, role);
  } catch {
    /* ignore storage failures — dev only */
  }
};

export const clearDevRole = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

/** The bearer token the API expects for a given dev role. */
export const devTokenFor = (role: DevRoleKey): string => `${DEV_TOKEN_PREFIX}${role}`;

export const devRoleOption = (role: DevRoleKey): DevRoleOption =>
  DEV_ROLES.find((r) => r.key === role) ?? DEV_ROLES[0];
